import { z } from "zod/v4";

/** Field types the form builder supports. `group` is a repeatable set of leaf fields.
 *  `consent` is legacy: kept so stored schemas still parse, never offered or
 *  accepted on save — consent is automatic on the visitor's self-fill page
 *  (spec 2026-09-26 §5). */
export const formFieldTypes = [
  "text",
  "id_number",
  "textarea",
  "number",
  "date",
  "select",
  "checkbox",
  "radio",
  "consent",
  "file",
  "group",
] as const;
export const formFieldTypeSchema = z.enum(formFieldTypes);
export type FormFieldType = (typeof formFieldTypes)[number];

/** What the form builder offers — every type except the legacy `consent`. */
export const builderFieldTypes = formFieldTypes.filter((t) => t !== "consent");

/** Which identity number an `id_number` field asks for. */
export const idKinds = ["cin", "passport"] as const;
export const idKindSchema = z.enum(idKinds);
export type IdKind = (typeof idKinds)[number];

/** Classification for uploaded documents — drives selective (CNDP) deletion later. */
export const documentKinds = [
  "cin",
  "passport",
  "acte_mariage",
  "other",
] as const;
export const documentKindSchema = z.enum(documentKinds);
export type DocumentKind = (typeof documentKinds)[number];

/** Lifecycle of a form definition. */
export const formStatuses = ["draft", "active", "archived"] as const;
export const formStatusSchema = z.enum(formStatuses);
export type FormStatus = (typeof formStatuses)[number];

/** Lifecycle of a filled submission. */
export const formSubmissionStatuses = [
  "pending",
  "approved",
  "rejected",
  "not_required",
] as const;
export const formSubmissionStatusSchema = z.enum(formSubmissionStatuses);
export type FormSubmissionStatus = (typeof formSubmissionStatuses)[number];

export const formFieldOptionSchema = z.object({
  value: z.string().trim().min(1).max(120),
  label: z.string().trim().min(1).max(200),
});
export type FormFieldOption = z.infer<typeof formFieldOptionSchema>;

/** Machine key: lower snake_case, starts with a letter. Stable across edits. */
const fieldKeySchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]*$/, "Clé invalide (minuscules, chiffres, _).")
  .max(64);

const baseFieldShape = {
  key: fieldKeySchema,
  label: z.string().trim().min(1).max(200),
  help: z.string().trim().max(500).optional(),
  required: z.boolean().default(false),
};

const leafTypeSchema = z.enum([
  "text",
  "id_number",
  "textarea",
  "number",
  "date",
  "select",
  "checkbox",
  "radio",
  "consent",
  "file",
]);

/** A non-group ("leaf") field. */
export const leafFieldSchema = z
  .object({
    ...baseFieldShape,
    type: leafTypeSchema,
    options: z.array(formFieldOptionSchema).optional(),
    accept: z.array(z.string().min(1)).optional(),
    documentKind: documentKindSchema.optional(),
    idKind: idKindSchema.optional(),
  })
  .superRefine((f, ctx) => {
    const needsOptions =
      f.type === "select" || f.type === "radio" || f.type === "checkbox";
    if (needsOptions && (!f.options || f.options.length === 0)) {
      ctx.addIssue({
        code: "custom",
        message: "Ce champ nécessite au moins une option.",
        path: ["options"],
      });
    }
  });
export type LeafField = z.infer<typeof leafFieldSchema>;

/** A repeatable group of leaf fields (e.g. the household-members table). */
export const groupFieldSchema = z.object({
  ...baseFieldShape,
  type: z.literal("group"),
  maxItems: z.number().int().min(1).max(50).optional(),
  fields: z.array(leafFieldSchema).min(1),
});
export type GroupField = z.infer<typeof groupFieldSchema>;

export const formFieldSchema = z.union([leafFieldSchema, groupFieldSchema]);
export type FormField = z.infer<typeof formFieldSchema>;

/** A full form definition: an ordered list of fields with unique keys per level. */
export const formSchemaSchema = z
  .object({
    fields: z.array(formFieldSchema).min(1).max(100),
  })
  .superRefine((val, ctx) => {
    const seen = new Set<string>();
    for (const f of val.fields) {
      if (seen.has(f.key)) {
        ctx.addIssue({
          code: "custom",
          message: `Clé dupliquée : ${f.key}`,
          path: ["fields"],
        });
      }
      seen.add(f.key);
      if (f.type === "group") {
        const childSeen = new Set<string>();
        for (const c of f.fields) {
          if (childSeen.has(c.key)) {
            ctx.addIssue({
              code: "custom",
              message: `Clé dupliquée dans le groupe ${f.key} : ${c.key}`,
              path: ["fields"],
            });
          }
          childSeen.add(c.key);
        }
      }
    }
  });
export type FormSchema = z.infer<typeof formSchemaSchema>;

function compileLeaf(field: LeafField): z.ZodTypeAny {
  switch (field.type) {
    case "text":
    case "textarea": {
      const s = z.string().trim().max(5000);
      return field.required ? s.min(1, "Champ requis.") : s.optional();
    }
    case "id_number": {
      const passport = field.idKind === "passport";
      const re = passport ? /^[A-Z0-9]{6,9}$/ : /^[A-Z]{1,2}[0-9]{3,6}$/;
      const message = passport
        ? "Numéro de passeport invalide."
        : "Numéro de CIN invalide (ex. BK123456).";
      const clean = (v: unknown) =>
        typeof v === "string" ? v.replace(/\s+/g, "").toUpperCase() : v;
      // An empty required number is missing, not malformed.
      return field.required
        ? z.preprocess(clean, z.string().min(1, "Champ requis.").regex(re, message))
        : z.preprocess(
            (v) => (clean(v) === "" ? undefined : clean(v)),
            z.string().regex(re, message).optional(),
          );
    }
    case "number": {
      const s = z.number();
      return field.required ? s : s.optional();
    }
    case "date": {
      const s = z.string().min(1, "Date requise.");
      return field.required ? s : s.optional();
    }
    case "select":
    case "radio": {
      const values = (field.options ?? []).map((o) => o.value);
      const oneOf = (v: unknown) => typeof v === "string" && values.includes(v);
      return field.required
        ? z.string().refine(oneOf, "Valeur invalide.")
        : z
            .string()
            .optional()
            .refine((v) => v === undefined || oneOf(v), "Valeur invalide.");
    }
    case "checkbox": {
      const values = (field.options ?? []).map((o) => o.value);
      const arr = z.array(
        z.string().refine((v) => values.includes(v), "Valeur invalide."),
      );
      return field.required ? arr.min(1, "Sélection requise.") : arr.optional();
    }
    case "consent": {
      return field.required
        ? z.boolean().refine((v) => v === true, "Consentement requis.")
        : z.boolean().optional();
    }
    case "file": {
      const s = z.string().min(1, "Document requis.");
      return field.required ? s : s.optional();
    }
  }
}

interface CompileOpts {
  skipFileFields?: boolean;
}

function compileField(field: FormField, opts: CompileOpts): z.ZodTypeAny {
  if (field.type === "group") {
    const childShape: Record<string, z.ZodTypeAny> = {};
    for (const child of field.fields) {
      // Legacy: consent is given on the self-fill page, never through the form.
      if (child.type === "consent") continue;
      if (child.type === "file" && opts.skipFileFields) continue;
      childShape[child.key] = compileLeaf(child);
    }
    let arr = z.array(z.object(childShape));
    if (field.maxItems !== undefined) {
      arr = arr.max(field.maxItems, `Maximum ${field.maxItems} éléments.`);
    }
    return field.required
      ? arr.min(1, "Au moins un élément requis.")
      : arr.optional();
  }
  return compileLeaf(field);
}

/**
 * Turn a validated FormSchema into a Zod validator for an answers object.
 * With `{ skipFileFields: true }`, file fields are omitted (Phase 3: uploads
 * are not yet wired). Unknown keys are stripped.
 */
export function compileFormSchema(
  schema: FormSchema,
  opts: CompileOpts = {},
): z.ZodType<Record<string, unknown>> {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const field of schema.fields) {
    // Legacy: consent is given on the self-fill page, never through the form.
    if (field.type === "consent") continue;
    if (field.type === "file" && opts.skipFileFields) continue;
    shape[field.key] = compileField(field, opts);
  }
  return z.object(shape);
}

/** The visitor types that can have a form — all three (spec 2026-09-23 §2.4).
 *  No form for a type means exactly the pre-form behaviour. */
export const formVisitorTypes = ["visiteur", "locataire", "intervenant"] as const;
export const formVisitorTypeSchema = z.enum(formVisitorTypes);
export type FormVisitorType = (typeof formVisitorTypes)[number];

export const formVisitorTypeLabels: Record<FormVisitorType, string> = {
  visiteur: "Visiteur",
  locataire: "Locataire",
  intervenant: "Intervenant",
};

/** Create (no id) or edit (id) a form definition. Server only edits drafts. */
export const formUpsertInput = z.object({
  id: z.string().min(1).optional(),
  visitorType: formVisitorTypeSchema,
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(1000).optional(),
  schema: formSchemaSchema,
  requiresApproval: z.boolean().default(false),
}).superRefine((v, ctx) => {
  const hasConsent = v.schema.fields.some(
    (f) => f.type === "consent" || (f.type === "group" && f.fields.some((c) => c.type === "consent")),
  );
  if (hasConsent)
    ctx.addIssue({
      code: "custom",
      message: "Le consentement est ajouté automatiquement : retirez ce champ.",
      path: ["schema"],
    });
});
export type FormUpsertInput = z.infer<typeof formUpsertInput>;

/** Returns a copy of the schema with all file fields removed (top-level + group children). */
export function stripFileFields(schema: FormSchema): FormSchema {
  return {
    fields: schema.fields
      .filter((f) => f.type !== "file")
      .map((f) =>
        f.type === "group"
          ? { ...f, fields: f.fields.filter((c) => c.type !== "file") }
          : f,
      ),
  };
}

/** Syndic review of a submission. */
export const formReviewInput = z.object({
  submissionId: z.string().min(1),
  decision: z.enum(["approve", "reject"]),
  note: z.string().trim().max(1000).optional(),
});
export type FormReviewInput = z.infer<typeof formReviewInput>;

/** « Tout valider » on a Validations group card — one transaction, the same
 *  per-row guards as `form.review`; rows no longer pending are reported. */
export const formReviewManyInput = z.object({
  submissionIds: z.array(z.string().min(1)).min(1).max(100),
  decision: z.enum(["approve", "reject"]),
  note: z.string().trim().max(1000).optional(),
});
export type FormReviewManyInput = z.infer<typeof formReviewManyInput>;

/** Whether a document kind is personal data (identity docs). */
export function piiForDocumentKind(kind?: DocumentKind): boolean {
  return kind === "cin" || kind === "passport" || kind === "acte_mariage";
}

/**
 * How an upload's bytes came to be deleted — the *process*, kept apart from
 * `deletionReason` (the free-text note a syndic types). Recorded on every
 * deletion so a CNDP access request can be answered with both "when was it
 * erased" and "what erased it".
 */
export const uploadDeletionSources = [
  /** A syndic deleted one document, or purged a submission's documents. */
  "admin",
  /** Automatic: the pass stopped being usable and its retention ran out. */
  "retention",
  /** The pass the document belonged to was deleted. */
  "pass_deleted",
  /** Presigned but never linked to a pass, or detached when one was deleted. */
  "abandoned",
  /** The whole résidence was purged. */
  "org_purge",
] as const;
export const uploadDeletionSourceSchema = z.enum(uploadDeletionSources);
export type UploadDeletionSource = (typeof uploadDeletionSources)[number];

/**
 * Days an identity document is kept after its pass stops being usable
 * (expired, revoked, or rejected). Matches the arrivée-imprévue ID-number
 * window: a realistic claims/dispute period, after which holding a copy of
 * someone's CIN is not defensible under loi 09-08.
 */
export const IDENTITY_DOCUMENT_RETENTION_DAYS = 90;

/**
 * Days an upload with no pass attached is kept. Covers both the presign that
 * was never submitted and the row detached when its pass was deleted. Long
 * enough that no realistic form fill is swept mid-flight, short enough that
 * an identity document with nothing referencing it does not linger.
 */
export const ABANDONED_UPLOAD_GRACE_DAYS = 7;

/** Max upload size accepted for a form file (10 MB). */
export const MAX_FORM_UPLOAD_BYTES = 10 * 1024 * 1024;

/**
 * The only content types a visitor-form file field may hold: photos/scans of
 * a document and PDFs. Signed into the presigned PUT, so a client cannot park
 * HTML/JS/SVG (anything a browser would execute) in the documents keyspace.
 * HEIC/HEIF are what iOS cameras produce.
 */
export const FORM_UPLOAD_CONTENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
  "application/pdf",
] as const;
export type FormUploadContentType = (typeof FORM_UPLOAD_CONTENT_TYPES)[number];

/** Type guard for the allow-list above — clients check the picked file's
 *  MIME type with this before asking for a presign, so an unsupported file
 *  gets a precise message instead of a generic 400. */
export function isFormUploadContentType(
  value: string | null | undefined,
): value is FormUploadContentType {
  return (FORM_UPLOAD_CONTENT_TYPES as readonly string[]).includes(value ?? "");
}

/** Human list for error copy / file-picker hints. */
export const FORM_UPLOAD_FORMATS_LABEL = "JPG, PNG, WebP, HEIC ou PDF";

export const formUploadUrlInput = z.object({
  fieldKey: z.string().min(1).max(64),
  documentKind: documentKindSchema.optional(),
  fileName: z.string().trim().min(1).max(255),
  contentType: z.enum(FORM_UPLOAD_CONTENT_TYPES),
  byteSize: z.number().int().positive().max(MAX_FORM_UPLOAD_BYTES),
});
export type FormUploadUrlInput = z.infer<typeof formUploadUrlInput>;

export const formUploadDeleteInput = z.object({
  uploadId: z.string().min(1),
  reason: z.string().trim().min(1).max(500),
});
export type FormUploadDeleteInput = z.infer<typeof formUploadDeleteInput>;

export const formPurgeSubmissionInput = z.object({
  submissionId: z.string().min(1),
  reason: z.string().trim().min(1).max(500),
});
export type FormPurgeSubmissionInput = z.infer<typeof formPurgeSubmissionInput>;

/** All file-field answer ids referenced by a submission (top-level + groups). */
export function collectUploadIds(
  schema: FormSchema,
  answers: Record<string, unknown>,
): string[] {
  const ids = new Set<string>();
  const add = (v: unknown) => {
    if (typeof v === "string" && v.length > 0) ids.add(v);
  };
  for (const field of schema.fields) {
    if (field.type === "file") {
      add(answers[field.key]);
    } else if (field.type === "group") {
      const rows = answers[field.key];
      if (Array.isArray(rows)) {
        for (const row of rows) {
          if (row && typeof row === "object") {
            for (const child of field.fields) {
              if (child.type === "file") {
                add((row as Record<string, unknown>)[child.key]);
              }
            }
          }
        }
      }
    }
  }
  return [...ids];
}

/** Whether a dossier still holds a typed identity number (a non-empty
 *  `id_number` answer, top level or inside a group) — the review screen offers
 *  the purge action for it even when the dossier has no upload (spec §5). */
export function hasIdNumberAnswers(
  schema: FormSchema,
  answers: Record<string, unknown>,
): boolean {
  const filled = (v: unknown) => typeof v === "string" && v.trim() !== "";
  return schema.fields.some((f) => {
    if (f.type === "id_number") return filled(answers[f.key]);
    if (f.type !== "group" || !Array.isArray(answers[f.key])) return false;
    const idKeys = f.fields.filter((c) => c.type === "id_number").map((c) => c.key);
    return (answers[f.key] as Record<string, unknown>[]).some((item) =>
      idKeys.some((k) => filled(item[k])),
    );
  });
}

/** Blanks every `id_number` answer (top level and inside groups) — called by
 *  each path that purges a dossier's identity documents (spec §5). */
export function redactIdNumbers(
  schema: FormSchema,
  answers: Record<string, unknown>,
): { answers: Record<string, unknown>; changed: boolean } {
  let changed = false;
  const out: Record<string, unknown> = { ...answers };
  for (const f of schema.fields) {
    if (f.type === "id_number" && out[f.key] != null) {
      out[f.key] = null;
      changed = true;
    } else if (f.type === "group" && Array.isArray(out[f.key])) {
      const idKeys = f.fields.filter((c) => c.type === "id_number").map((c) => c.key);
      if (idKeys.length === 0) continue;
      out[f.key] = (out[f.key] as Record<string, unknown>[]).map((item) => {
        const next = { ...item };
        for (const k of idKeys)
          if (next[k] != null) {
            next[k] = null;
            changed = true;
          }
        return next;
      });
    }
  }
  return { answers: out, changed };
}
